import moment from "moment";
import {
  dateTimeFormatted,
  formatAppointmentDateTime,
  formattedDate,
} from "../utils/dateUtils";
import api from "./Api";
import { handleApiError } from "../utils/apiUtils";
import { AxiosProgressEvent } from "axios";
import { preventDuplicateRequest } from "../utils/requestManager";
import { TRANSCRIPTION_REPORT_STATUS } from "../common/Constant";

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

export interface AssignedUser {
  userId: number;
  username: string;
  fullname: string;
  emailId: string;
  departmentName: string | null;
  genderName: string | null;
  phoneNumber: string | null;
  userTypeName: string;
  userRoleName: string;
  teamName: string | null;
}

export interface UpdateAssignedToRequest {
  subjectQmeScheduleIDs: string;
  assignTo: number;
}

interface DocumentCategoryItem {
  documentTypeId: number | string;
  documentType: string;
  documentCategory: string;
  documentTypeDescription: string;
}

interface DocumentTypeOption {
  value: string;
  label: string;
  documentCategory: string;
  documentTypeDescription: string;
}

const editor = process.env.REACT_APP_CASE_URL || "";
if (!editor) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables.",
  );
}

const EditorAPI = api(editor);

const getDoctorSummaryCard = async (editorData: any): Promise<any> => {
  const key = `getDoctorSummaryCard-${JSON.stringify(editorData)}`;
  return preventDuplicateRequest(key, async () => {
    try {
      const response = await EditorAPI.post<ApiResponse<any>>(
        "/api/qme-doctor/summary",
        editorData,
      );

      return response.data?.result;
    } catch (error) {
      console.error("Error adding a new user:", error);
      throw new Error(
        "Failed to add the user. Please check the data and try again.",
      );
    }
  });
};

const getDoctorGridSearch = async (editorData: any): Promise<any> => {
  const key = `getDoctorGridSearch-${JSON.stringify(editorData)}`;
  return preventDuplicateRequest(key, async () => {
    try {
      const response = await EditorAPI.post<ApiResponse<any>>(
        "/api/qme-doctor/schedule",
        editorData,
      );
      const updatedResult = response.data?.result.map((data: any) => ({
        ...data,
        subjectqmescheduleid: data.subjectQmeScheduleId,
        appointmentDateTime: formatAppointmentDateTime(
          data.appointmentDateTime,
        ),
        dueDate: formattedDate(data.dueDate),
        format_dueDate: formatAppointmentDateTime(data.dueDate),
        reportStatus:
          data.isDictationAvailable === 0 &&
            data.transcriptionItemStatusName === "TranscriptionNotInitiated" &&
            data.reportStatus === "Editing Not Initiated"
            ? "Dictation Pending"
            : data.isDictationAvailable === 1 &&
              data.transcriptionItemStatusName === "TranscriptionPending"
              ? TRANSCRIPTION_REPORT_STATUS.TRANSCRIPTION_PENDING
              : data.isDictationAvailable === 1 &&
                data.transcriptionItemStatusName === "TranscriptionInProgress"
                ? "Transcription In Progress"
                : data.reportStatus,
      }));

      return updatedResult;
    } catch (error) {
      console.error("Error adding a new user:", error);
      throw new Error(
        "Failed to add the user. Please check the data and try again.",
      );
    }
  });
};

const GetAssignedTo = async (): Promise<
  ApiResponse<AssignedUser[]> | undefined
> => {
  try {
    const response = await EditorAPI.get(`/api/qme-doctor/assignee/list`);
    return response.data;
  } catch (error) {
    console.error("Error:", error);
    return undefined;
  }
};

const updateAssignedTo = async (
  data: UpdateAssignedToRequest,
): Promise<ApiResponse<any>> => {
  try {
    const response = await EditorAPI.put<ApiResponse<any>>(
      "/api/editor/assignee/update",
      data,
    );
    return response.data;
  } catch (error) {
    throw new Error("Unable to update assigned To. Please try again later.");
  }
};

const updateQMEDoctorStatus = async (data: any): Promise<ApiResponse<any>> => {
  try {
    const response = await EditorAPI.post<ApiResponse<any>>(
      "/api/qme-doctor/status/update",
      data,
    );
    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getApproveReworkLogsList = async (
  subjectQmeScheduleId: Number,
): Promise<ApiResponse<any[]> | undefined> => {
  try {
    const response = await EditorAPI.get(
      `/api/qme-doctor/status-log/${subjectQmeScheduleId}`,
    );

    return {
      ...response.data,
      result:
        response.data.result?.map((item: any) => ({
          ...item,
          doctorQMEStatusName: item.editorStatusName,
          createdDateNew: dateTimeFormatted(item.createdDate),
        })) || [],
    };
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const downloadZipFileForRecordDownloadAll = async (
  caseInfoIdNew: number,
  SubjectQmeScheduleId: number,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  try {
    const response = await EditorAPI.get(
      `/api/qme-doctor/records/downloadAll?CaseInfoId=${caseInfoIdNew}&SubjectQmeScheduleId=${SubjectQmeScheduleId}`,
      {
        responseType: "blob",
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
            onProgress(percentCompleted);
          }
        },
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let fileNameMatch =
      contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;\n]+)/) ||
      contentDisposition?.match(/filename="?([^";\n]+)"?/);

    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1])
      : "download.zip";

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const fetchDoctorDocumentTypeByAppointment = async (
  subjectQmeScheduleId: any,
): Promise<DocumentTypeOption[]> => {
  try {
    const response = await EditorAPI.get(
      `/api/qme-doctor/document/by-appointment-type?subjectQmeScheduleId=${subjectQmeScheduleId}`,
    );
    const list = response.data?.result.map((item: DocumentCategoryItem) => ({
      value: item.documentTypeId,
      label: item.documentType,
      documentCategory: item.documentCategory,
      documentTypeDescription: item.documentTypeDescription,
    }));
    return list;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export {
  getDoctorSummaryCard,
  getDoctorGridSearch,
  GetAssignedTo,
  updateAssignedTo,
  updateQMEDoctorStatus,
  getApproveReworkLogsList,
  downloadZipFileForRecordDownloadAll,
  fetchDoctorDocumentTypeByAppointment,
};
